import { buildInfo } from "../generated/buildInfo";

const windowConfig =
  typeof window !== "undefined" && window.__APP_CONFIG__
    ? window.__APP_CONFIG__
    : {};

function getConfigValue(key, fallback = "") {
  const runtimeValue = windowConfig[key];
  if (runtimeValue !== undefined && runtimeValue !== null && runtimeValue !== "") {
    return runtimeValue;
  }

  const buildValue = process.env[key];
  if (buildValue !== undefined && buildValue !== null && buildValue !== "") {
    return buildValue;
  }

  return fallback;
}

// MIRA-2.1: strip trailing slash(es) so EVERY consumer inherits a normalized base. axios tolerates
// a trailing slash, but the raw streaming fetch (reflectiveApi.respondStream) concatenates
// `${baseUrl}/api/...` — a trailing slash yielded `host//api/...` → FastAPI 404 → the stream failed
// on every turn. Normalizing here fixes it for all callers, not just streaming. Exported so it can
// be unit-tested purely (no module reload).
export function normalizeBaseUrl(value) {
  return String(value || "").replace(/\/+$/, "");
}

export const runtimeConfig = {
  backendUrl: normalizeBaseUrl(getConfigValue("REACT_APP_BACKEND_URL")),
  demoMode: getConfigValue("REACT_APP_DEMO_MODE", "false") === "true",
  registrationApprovalRequired:
    getConfigValue("REACT_APP_REGISTRATION_APPROVAL_REQUIRED", "true") !== "false",
  // ⚑ C-399 §1 / F-06 / C-403 — THE RUNTIME VALUE FIRST, THEN THE BUILD-TIME STAMP, because on the
  // host that actually serves users there IS no runtime value. `REACT_APP_BUILD_SHA` reaches the
  // bundle only through start-frontend.sh's heredoc, and app.cognivio.live is CLOUDFLARE PAGES,
  // which runs a build and no start command — so that door does not exist there, however the
  // Railway service is configured. (Its settings were in fact correct all along; it simply has no
  // custom domain and serves no one. docs/FRONTEND-HOSTING.md.)
  //
  // `prebuild` runs on every build path because it is npm's contract rather than the platform's,
  // which is exactly why it survives the host turning out to be one nobody had named.
  buildSha: getConfigValue("REACT_APP_BUILD_SHA") || buildInfo.sha,
  buildTime: getConfigValue("REACT_APP_BUILD_TIME") || buildInfo.builtAt,
  // Which of the two answered, so a stale or unstamped bundle cannot read as a current one.
  buildShaSource: getConfigValue("REACT_APP_BUILD_SHA") ? "runtime-config" : buildInfo.source,
  dashboardV2Enabled: getConfigValue("REACT_APP_DASHBOARD_V2", "true") !== "false",
  dashboardRoleShellEnabled:
    getConfigValue("REACT_APP_DASHBOARD_ROLE_SHELL", "true") !== "false",
  dashboardDualModeEnabled:
    getConfigValue("REACT_APP_DASHBOARD_DUAL_MODE", "true") !== "false",
  dashboardOperationsLaneEnabled:
    getConfigValue("REACT_APP_DASHBOARD_OPERATIONS_LANE", "true") !== "false",
  dashboardInsightsLaneEnabled:
    getConfigValue("REACT_APP_DASHBOARD_INSIGHTS_LANE", "true") !== "false",
  dashboardSecondaryOpsDemoteEnabled:
    getConfigValue("REACT_APP_DASHBOARD_SECONDARY_OPS_DEMOTE", "true") !== "false",
  dashboardSmartQueueEnabled:
    getConfigValue("REACT_APP_DASHBOARD_SMART_QUEUE", "true") !== "false",
  guidedOnboardingEnabled:
    getConfigValue("REACT_APP_GUIDED_ONBOARDING", "true") !== "false",
  improvedEmptyStatesEnabled:
    getConfigValue("REACT_APP_IMPROVED_EMPTY_STATES", "true") !== "false",
  teacherCreationModalEnabled:
    getConfigValue("REACT_APP_TEACHER_CREATION_MODAL", "true") !== "false",
  schoolManagementSubflowEnabled:
    getConfigValue("REACT_APP_SCHOOL_MANAGEMENT_SUBFLOW", "true") !== "false",
  teacherRowQuickActionsEnabled:
    getConfigValue("REACT_APP_TEACHER_ROW_QUICK_ACTIONS", "true") !== "false",
  rosterHierarchyCleanupEnabled:
    getConfigValue("REACT_APP_ROSTER_HIERARCHY_CLEANUP", "true") !== "false",
  trainingModeFoundationEnabled:
    getConfigValue("REACT_APP_TRAINING_MODE_FOUNDATION", "false") === "true",
  assessmentFeedbackEnabled:
    getConfigValue("REACT_APP_ASSESSMENT_FEEDBACK", "true") !== "false",
  aiIntensityMode: getConfigValue("REACT_APP_AI_INTENSITY_MODE", "guided"),
  // CONSENT-DEVICE-1 — remembering camera consent per device, behind its own flag. DEFAULT OFF in
  // this config so a build that has not been switched on asks every time, which is the safe
  // direction for a consent prompt; staging sets it on and the owner switches production.
  //
  // THE FLAG ONLY EVER TURNS REMEMBERING OFF. With it off the consent screen is always shown and
  // every acceptance is still recorded in the ledger — it is not a kill switch for the ledger, and
  // it must never become one, because the ledger is the audit record the owner asked for.
  consentDeviceRemember: getConfigValue("REACT_APP_CONSENT_DEVICE_REMEMBER", "false") === "true",
  // RECORD-FLAG-ONE-1 — the client build flag for in-app recording is GONE. It was an OR beside
  // the server's per-user field, which meant switching it on granted recording to every teacher
  // served that build at once, and left two switches that could disagree. The server's
  // `record_live_enabled` is now the single one. See features/record/recordAccess.js.
  // RECORD-1 rollout: there is NO allowlist here on purpose. A configured list of people in a
  // PUBLIC bundle names those people to every browser that loads the app, so the per-user decision
  // is the server's and the client only reads the boolean it provides. See
  // features/record/recordAccess.js (COMMAND 7 Oct 19:46).
  experimentalMomentRankingEnabled:
    getConfigValue("REACT_APP_EXPERIMENTAL_MOMENT_RANKING", "false") === "true",
};


// ⚑ C-399 §1 — PUBLISHED ON `window` BECAUSE NOTHING CONSUMED IT. Measured across `frontend/src`
// before this change: `REACT_APP_BUILD_SHA` was READ twice and `buildSha` consumed ZERO times, so
// even a correctly configured service had nowhere to show it. One global, readable in a console in
// one line, costs no screen and needs no copy.
if (typeof window !== "undefined") {
  window.__BUILD_INFO__ = {
    sha: runtimeConfig.buildSha,
    source: runtimeConfig.buildShaSource,
    builtAt: runtimeConfig.buildTime,
    configSource: windowConfig.CONFIG_SOURCE || "absent",
  };
}
